import React, { useState }  from 'react';
import { observer } from 'mobx-react-lite';

import useStores from '../hooks/useStores';
import { DedicatedAssignment } from '../stores/dedicated_assignment_store';

export type DedicatedAssignmentContextType = {
    hasDedicatedAssistant: (identityKey: string) => boolean;
    isDedicatedFor: (assistantIdentityKey: string, identityKey: string) => boolean;
    getDedicatedAssignmentsByIdentity: (identityKey: string) => DedicatedAssignment[],
    getDedicatedAssignmentsByAssistantIdentity: (identityKey: string) => DedicatedAssignment[],
    supportAssignDedicatedAssistant: (identityKey: string, assistantIdentityKey: string, ranking: number) => Promise<DedicatedAssignment|void>;
    supportRemoveDedicatedAssistant: (identityKey: string, assistantIdentityKey: string) => Promise<void>;
    fetchDedicatedAssignmentsByAssistantIdentity: (identityKey: string) => Promise<DedicatedAssignment[]|void>;
    fetchDedicatedAssignmentsByIdentity: (workKey: string, client_identity: string) => Promise<DedicatedAssignment[]|void>;
    getDedicatedRanking: (assistantIdentityKey: string, identityKey: string) => number|undefined;
    fetchAll: (workKey: string, cursor?: string) => Promise<DedicatedAssignment[]|void>;
    items: DedicatedAssignment[];
};


const DedicatedAssignmentContext = React.createContext<DedicatedAssignmentContextType>({
    hasDedicatedAssistant: () => false,
    isDedicatedFor: () => false,
    getDedicatedAssignmentsByIdentity: () => [] as DedicatedAssignment[],
    getDedicatedAssignmentsByAssistantIdentity: () => [] as DedicatedAssignment[],
    supportAssignDedicatedAssistant: () => { throw "not implemented" },
    supportRemoveDedicatedAssistant: () => { throw "not implemented" },
    fetchDedicatedAssignmentsByAssistantIdentity: () => { throw "not implemented" },
    fetchDedicatedAssignmentsByIdentity: () => { throw "not implemented" },
    getDedicatedRanking: () => { throw "not implemented" },
    fetchAll: () => { throw "not implemented" },
    items: [] as DedicatedAssignment[]
});


const DedicatedAssignmentProvider = observer(({ children }: { children: React.ReactNode  })=> {

    const [ dedicatedAssignments, setDedicatedAssignments] = useState([] as DedicatedAssignment[]);
    const { dedicatedAssignmentStore } = useStores();
    //const { currentIdentity } = useUserIdentity();

    const getDedicatedAssignmentsByIdentity = (identityKey: string) => {
        return dedicatedAssignments.filter((r) => r.identity === identityKey)
            .sort((a, b) => {
                return (a.ranking > b.ranking) ? 1 : -1;
            });
    }

    const getDedicatedAssignmentsByAssistantIdentity = (identityKey: string) => {
        // console.log("getDedicatedAssignmentsByAssistantIdentity", identityKey, dedicatedAssignments, dedicatedAssignments.length)
        return dedicatedAssignmentStore.items.filter((r) => r.assistant_identity === identityKey)
            .sort((a, b) => {
                return (a.ranking > b.ranking) ? 1 : -1;
            });
    }

    const fetchDedicatedAssignmentsByAssistantIdentity = async (identityKey: string) => {

        return dedicatedAssignmentStore.fetchByAssistantIdentity(identityKey)
            .then(() => {
                // setDedicatedAssignments(dedicatedAssignmentStore.items);
                return dedicatedAssignmentStore.items;
            });
    }

    const fetchDedicatedAssignmentsByIdentity = async (workKey: string, client_identity: string) => {
        return dedicatedAssignmentStore.fetchByIdentity(workKey, client_identity)
            .then(() => {
                setDedicatedAssignments(dedicatedAssignmentStore.items);
                return dedicatedAssignmentStore.items;
            });
    }


    const supportAssignDedicatedAssistant = (identityKey: string, assistantIdentityKey: string, ranking: number) => {
        return dedicatedAssignmentStore.supportAssign(identityKey, assistantIdentityKey, ranking)
            .then((da: DedicatedAssignment) => {
                fetchDedicatedAssignmentsByIdentity(identityKey, assistantIdentityKey).then(() => {
                return da;
                });
            });
    }

    const supportRemoveDedicatedAssistant = async (identityKey: string, assistantIdentityKey: string) => {
        return dedicatedAssignmentStore.supportRemove(identityKey, assistantIdentityKey)
            .then(() => {
                setDedicatedAssignments(dedicatedAssignmentStore.items);
            });
    }

    const hasDedicatedAssistant = (identityKey: string) => {
        return getDedicatedAssignmentsByIdentity(identityKey).length > 0;
    }

    const isDedicatedFor = (assistantIdentityKey: string, identityKey: string) => {
        //console.log("getDedicatedAssignmentsByAssistantIdentity", assistantIdentityKey, getDedicatedAssignmentsByAssistantIdentity(assistantIdentityKey))
        return getDedicatedAssignmentsByAssistantIdentity(assistantIdentityKey).filter((r) => identityKey === r.identity).length > 0;
    }

    const getDedicatedRanking = (assistantIdentityKey: string, identityKey: string) => {
        return getDedicatedAssignmentsByAssistantIdentity(assistantIdentityKey).filter((r) => identityKey === r.identity)[0]?.ranking;
    }

    const fetchAll = (workKey: string, cursor?: string) => {
        return dedicatedAssignmentStore.fetchAll(workKey, cursor)
            .then(() => {
                return dedicatedAssignmentStore.items;
            });
    }

    const data = {
        hasDedicatedAssistant: hasDedicatedAssistant,
        isDedicatedFor: isDedicatedFor,
        getDedicatedAssignmentsByIdentity: getDedicatedAssignmentsByIdentity,
        getDedicatedAssignmentsByAssistantIdentity: getDedicatedAssignmentsByAssistantIdentity,
        supportAssignDedicatedAssistant: supportAssignDedicatedAssistant,
        supportRemoveDedicatedAssistant: supportRemoveDedicatedAssistant,
        fetchDedicatedAssignmentsByAssistantIdentity: fetchDedicatedAssignmentsByAssistantIdentity,
        fetchDedicatedAssignmentsByIdentity: fetchDedicatedAssignmentsByIdentity,
        getDedicatedRanking: getDedicatedRanking,
        fetchAll: fetchAll,
        items: dedicatedAssignmentStore.items
    }

    return (
        <DedicatedAssignmentContext.Provider value={data}>{ children }</DedicatedAssignmentContext.Provider>
    )

});

export { DedicatedAssignmentContext, DedicatedAssignmentProvider };
